// Quote list. Products added with "Add to quote" live here instead of the Shopify cart, so they
// never reach checkout or the cart totals; only the header badge adds them to the cart count
// (CartDrawer.updateCartIconBubble). Saved per browser in localStorage
// (falls back to memory when storage is blocked); the cart drawer Quote tab renders it.
const QuoteList = {
  KEY: 'quote-list:items',
  // the most of one item the quotes app takes on a quote
  MAX_QUANTITY: 10000,
  memory: [],

  read() {
    try {
      const items = JSON.parse(localStorage.getItem(this.KEY));
      return Array.isArray(items) ? items : [];
    } catch {
      return this.memory;
    }
  },

  write(items) {
    this.memory = items;
    try {
      localStorage.setItem(this.KEY, JSON.stringify(items));
    } catch {
      // storage blocked (private mode etc.), the in-memory copy still works for this page
    }
    document.dispatchEvent(new CustomEvent('quote-list:change'));
  },

  count() {
    return this.read().reduce((total, item) => total + item.quantity, 0);
  },

  add(item) {
    this.write(this.reconcileVariantQuantities([...this.read(), item]));
  },

  setQuantity(variantId, quantity) {
    this.write(
      this.read()
        .map((item) => (item.variant_id === variantId ? { ...item, quantity: this.withinMaxQuantity(quantity) } : item))
        .filter((item) => item.quantity > 0)
    );
  },

  remove(variantId) {
    this.setQuantity(variantId, 0);
  },

  clear() {
    this.write([]);
  },

  // One row per variant, like the cart: a variant listed again adds its quantity to its first row,
  // within MAX_QUANTITY, and that row keeps its place.
  reconcileVariantQuantities(items) {
    return items.reduce((rows, item) => {
      const existing = this.findItem(rows, item.variant_id);
      if (existing) existing.quantity = this.withinMaxQuantity(existing.quantity + item.quantity);
      else rows.push({ ...item, quantity: this.withinMaxQuantity(item.quantity) });
      return rows;
    }, []);
  },

  findItem(items, variantId) {
    return items.find((item) => item.variant_id === variantId);
  },

  withinMaxQuantity(quantity) {
    return Math.min(quantity, this.MAX_QUANTITY);
  },
};

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.setRequestQuoteTriggers();
    this.tabs = new CartDrawerTabs(this);
    this.setCartIconBubbleCount();
  }

  // The header badge counts cart items plus quote items. Liquid only knows the cart, so the
  // quote count is added here on load, on every quote list change, and after every cart update.
  // Cart updates replace #cart-icon-bubble's children from the cart-icon-bubble section (cart.js,
  // product-form.js, quick-add...), which the observer catches without hooking each of them.
  setCartIconBubbleCount() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    const update = () => this.updateCartIconBubble(cartLink);
    document.addEventListener('quote-list:change', update);
    window.addEventListener('storage', (event) => event.key === QuoteList.KEY && update());
    // childList only: update() edits the badge's own children, so it never re-triggers this
    new MutationObserver(update).observe(cartLink, { childList: true });
    update();
  }

  updateCartIconBubble(cartLink) {
    const bubble = cartLink.querySelector('.cart-count-bubble');
    if (!bubble) return;

    const total = (parseInt(bubble.dataset.cartCount, 10) || 0) + QuoteList.count();
    // same rules as the Liquid badge: hidden (:empty) at 0, no number from 100 up
    if (!total) {
      bubble.replaceChildren();
      return;
    }
    const count = document.createElement('span');
    count.setAttribute('aria-hidden', 'true');
    if (total < 100) count.textContent = total;
    bubble.replaceChildren(count);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  // "Request a Quote" triggers live outside the drawer (PDP button, collection and accessory cards),
  // so the click is delegated off document rather than off <cart-drawer> like the tab handlers below.
  setRequestQuoteTriggers() {
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-request-quote]');
      if (!trigger) return;

      // also stops the native submit (and the card's customAddToCart submit handler)
      event.preventDefault();
      if (trigger.disabled || trigger.getAttribute('aria-disabled') === 'true' || this.requestingQuote) return;

      // the PDP and accessory cards wrap their form in <product-form>; collection cards are a bare form
      const scope = trigger.closest('product-form') || trigger.form;
      const variantId = scope?.querySelector('input[name="id"]')?.value;
      // product-info.js / card-variant-selects re-render this input on variant change, so it holds the live SKU
      const partNumber = scope?.querySelector('input[name="properties[Part Number]"]')?.value;
      // cards: the visible stepper (they also carry a hidden quantity input);
      // PDP: the quantity input sits outside the form and joins it through its form attribute
      const quantityInput = trigger.form?.querySelector('.quantity__input') || trigger.form?.elements.quantity;
      const quantity = parseInt(quantityInput?.value, 10) || 1;
      if (!variantId) return;

      // same loading state product-form.js gives the Add to cart button
      const spinner = trigger.querySelector('.loading__spinner');
      trigger.setAttribute('aria-disabled', 'true');
      trigger.classList.add('loading');
      spinner?.classList.remove('hidden');
      this.requestQuote(variantId, trigger, partNumber, quantity).finally(() => {
        trigger.removeAttribute('aria-disabled');
        trigger.classList.remove('loading');
        spinner?.classList.add('hidden');
      });
    });
  }

  // Saves the selected variant to the quote list (not the cart), then opens the drawer on the
  // Quote tab. Product details come from the product's .js endpoint so the row can be drawn later.
  // Also called by the PDP variant table (variant-table.js); resolves to whether the item was added.
  async requestQuote(variantId, triggeredBy, partNumber, quantity = 1) {
    this.requestingQuote = true;
    this.setActiveElement(triggeredBy);
    // errors show in the PDP product form's own message area, same as Add to cart
    const productForm = triggeredBy.closest('product-form');
    productForm?.handleErrorMessage?.();

    try {
      const loadProduct = async (url) => {
        const response = await fetch(`${url}.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(`Could not load product (${response.status})`);
        return response.json();
      };
      const findVariant = (entry) => String(entry.id) === String(variantId);

      let productUrl = triggeredBy.dataset.productUrl;
      let product = await loadProduct(productUrl);
      let variant = product.variants.find(findVariant);
      if (!variant) {
        // SellersDash parent cards mix variants of their child products; /variants/:id redirects
        // to the product that owns the variant (HEAD: only the final URL is needed, not the page)
        const owner = await fetch(`${window.Shopify?.routes?.root || '/'}variants/${variantId}`, { method: 'HEAD' });
        productUrl = new URL(owner.url).pathname;
        product = await loadProduct(productUrl);
        variant = product.variants.find(findVariant);
      }
      if (!variant) throw new Error('Selected variant not found');

      const hasOnlyDefaultVariant = product.variants.length === 1 && variant.title === 'Default Title';
      const optionNames = (product.options || []).map((option) => (typeof option === 'string' ? option : option.name));
      QuoteList.add({
        variant_id: variant.id,
        quantity,
        product_title: product.title,
        options: hasOnlyDefaultVariant
          ? []
          : optionNames.map((name, index) => ({ name, value: variant.options[index] })).filter((o) => o.value),
        sku: partNumber || variant.sku || '',
        price: variant.price,
        image: variant.featured_image?.src || product.featured_image || '',
        url: `${product.url || productUrl}?variant=${variant.id}`,
      });

      this.tabs.activate('quote');
      this.open(triggeredBy);
      return true;
    } catch (error) {
      console.error(error);
      productForm?.handleErrorMessage?.(window.cartStrings?.error || error.message);
      return false;
    } finally {
      this.requestingQuote = false;
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // section re-renders replace .drawer__inner, so the active tab has to be re-applied
    this.tabs.activate(this.tabs.active);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn =
          this.classList.contains('is-empty') && !this.classList.contains('has-quote')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

// The drawer's Cart / Quote tabs, and the Quote tab's rows of the quote list (QuoteList, above).
// Handlers are delegated off the <cart-drawer> element, which the section re-render never
// replaces, and the active tab is mirrored onto it for the same reason. A submit of the Quote tab's
// form goes to CartDrawerQuoteForm (assets/cart-drawer-quote-form.js).
class CartDrawerTabs {
  constructor(drawer) {
    this.drawer = drawer;
    this.$drawer = $(drawer);
    this.bindEvents();
  }

  get active() {
    return this.drawer.dataset.cartDrawerTab || 'cart';
  }

  bindEvents() {
    this.$drawer.on('click', '[data-cart-drawer-tab]', (event) => {
      event.preventDefault();
      this.activate(event.currentTarget.dataset.cartDrawerTab);
    });

    this.$drawer.on('keydown', '[data-cart-drawer-tab]', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      const $tabs = this.$drawer.find('[data-cart-drawer-tab]');
      const step = event.key === 'ArrowRight' ? 1 : -1;
      const $next = $tabs.eq(($tabs.index(event.currentTarget) + step + $tabs.length) % $tabs.length);
      this.activate($next[0].dataset.cartDrawerTab);
      $next.trigger('focus');
    });

    // Quote rows: the stepper and Remove only touch the quote list, never the cart
    this.$drawer.on('change', '[data-quote-quantity]', (event) => {
      const variantId = Number(event.target.closest('[data-quote-variant-id]').dataset.quoteVariantId);
      QuoteList.setQuantity(variantId, Math.max(1, parseInt(event.target.value, 10) || 1));
    });
    this.$drawer.on('click', '[data-quote-remove]', (event) => {
      QuoteList.remove(Number(event.currentTarget.closest('[data-quote-variant-id]').dataset.quoteVariantId));
    });

    // redraw on any quote list change, including from another browser tab
    document.addEventListener('quote-list:change', () => this.renderQuote());
    window.addEventListener('storage', (event) => event.key === QuoteList.KEY && this.renderQuote());

    // Cart updates re-render .drawer__inner from the server, which brings back the panels'
    // default hidden state and an empty quote list, so re-apply the tab and redraw the quote.
    // The observer also covers re-renders that publish nothing (e.g. the Rebuy handler in cart.js).
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.activate(this.active));
    new MutationObserver(() => {
      if (this.drawer.querySelector('[data-quote-items]:not([data-quote-rendered])')) this.activate(this.active);
    }).observe(this.drawer, { childList: true, subtree: true });
    this.renderQuote();

    this.$drawer.on('submit', '[data-cart-drawer-quote-form]', (event) => {
      event.preventDefault();
      new CartDrawerQuoteForm(event.currentTarget).submit();
    });

    // country -> state list, per address block (shipping and billing)
    this.$drawer.on('change', '[data-quote-country]', (event) => this.fillProvinces(event.target));

    this.$drawer.on('change', '[data-quote-billing-same]', () => this.syncBillingFields());

    this.$drawer.on('input change', '.cart-quote__form .is-invalid', (event) => {
      event.currentTarget.classList.remove('is-invalid');
    });
  }

  activate(name) {
    this.drawer.dataset.cartDrawerTab = name;
    this.prefillLocation();
    this.syncBillingFields();
    const $tab = this.$drawer.find(`[data-cart-drawer-tab="${name}"]`);
    const heading = $tab.attr('data-cart-drawer-tab-title');

    this.$drawer.find('[data-cart-drawer-tab]').each(function () {
      const isActive = this.dataset.cartDrawerTab === name;
      $(this)
        .toggleClass('is-active', isActive)
        .attr('aria-selected', isActive ? 'true' : 'false')
        .attr('tabindex', isActive ? '0' : '-1');
    });

    this.renderQuote();
    // With an empty cart and an empty quote list the tabs are hidden, so show the Cart panel's
    // empty message. The requested tab stays stored for when something is added.
    const nothingToShow = this.drawer.classList.contains('is-empty') && !QuoteList.read().length;
    const panel = nothingToShow ? 'cart' : name;
    this.$drawer.find('[data-cart-drawer-tab-panel]').each(function () {
      $(this).prop('hidden', this.dataset.cartDrawerTabPanel !== panel);
    });

    if (heading) this.$drawer.find('.drawer__heading').text(heading);
  }

  // Draws the quote list into the Quote tab from the <template> in cart-drawer.liquid, and flags
  // the drawer with has-quote so CSS shows the header and tabs even when the cart is empty.
  renderQuote() {
    const items = QuoteList.read();
    const count = QuoteList.count();
    this.drawer.classList.toggle('has-quote', items.length > 0);
    this.$drawer.find('[data-quote-count]').text(count ? `(${count})` : '');
    // the header circle follows the active tab, like the heading: cart items on Cart, quote items on Quote
    const $itemCount = this.$drawer.find('.drawer__header .item-count');
    $itemCount.text(this.active === 'quote' ? count : $itemCount.attr('data-cart-count'));

    const list = this.drawer.querySelector('[data-quote-items]');
    const template = this.drawer.querySelector('[data-quote-item-template]');
    if (!list || !template) return;

    list.replaceChildren(...items.map((item) => this.buildQuoteRow(template, item)));
    list.setAttribute('data-quote-rendered', '');
    this.$drawer.find('[data-quote-empty]').prop('hidden', items.length > 0);
    this.$drawer.find('[data-quote-content]').prop('hidden', items.length === 0);
  }

  buildQuoteRow(template, item) {
    const row = template.content.firstElementChild.cloneNode(true);
    row.dataset.quoteVariantId = item.variant_id;

    row.querySelectorAll('[data-quote-link]').forEach((link) => (link.href = item.url));
    row.querySelector('[data-quote-title]').textContent = item.product_title;
    const image = row.querySelector('[data-quote-image]');
    if (item.image) {
      image.src = `${item.image}${item.image.includes('?') ? '&' : '?'}width=300`;
      image.alt = item.product_title;
    } else {
      image.closest('[data-quote-link]').remove();
    }

    // same option / Part Number rows the cart item shows
    const options = [...(item.options || [])];
    if (item.sku) options.push({ name: 'Part Number', value: item.sku });
    const list = row.querySelector('[data-quote-options]');
    options.forEach(({ name, value }) => {
      const option = document.createElement('div');
      option.className = 'product-option';
      const term = document.createElement('dt');
      term.textContent = `${name}:`;
      const detail = document.createElement('dd');
      detail.textContent = value;
      option.append(term, detail);
      list.append(option);
    });
    if (!options.length) list.remove();

    row.querySelector('[data-quote-price]').textContent = this.formatMoney(item.price * item.quantity);

    const input = row.querySelector('[data-quote-quantity]');
    input.max = QuoteList.MAX_QUANTITY;
    input.value = item.quantity;
    input.setAttribute('value', item.quantity);
    input.setAttribute('aria-label', input.getAttribute('aria-label').replace('[title]', item.product_title));
    row.querySelectorAll('[data-quote-label]').forEach((label) => {
      label.textContent = label.textContent.replace('[title]', item.product_title);
    });
    return row;
  }

  // prices are stored in cents, as the product .js endpoint returns them
  formatMoney(cents) {
    const currency = window.Shopify?.currency?.active || 'USD';
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency }).format(
      (Number(cents) || 0) / 100
    );
  }

  // "Same as shipping address" hides the billing block and disables it, so it is neither
  // validated nor submitted. Also runs from activate(): a browser can restore the box unticked on
  // reload or back without a change event, and the block has to follow what the box shows.
  syncBillingFields() {
    const same = this.drawer.querySelector('[data-quote-billing-same]');
    if (same) this.$drawer.find('[data-quote-billing-fields]').prop({ hidden: same.checked, disabled: same.checked });
  }

  // Selects the logged-in customer's country and province in each address block (data-default,
  // see cart-quote-address.liquid). Runs from activate(), so a section re-render that brings back
  // a fresh form is prefilled again.
  prefillLocation() {
    this.drawer.querySelectorAll('[data-quote-country]:not([data-prefilled])').forEach((country) => {
      country.setAttribute('data-prefilled', '');
      if (!country.dataset.default) {
        // a browser can restore the country on reload or back, but not the states built for it
        if (country.value) this.fillProvinces(country);
        return;
      }

      country.value = country.dataset.default;
      if (country.value !== country.dataset.default) return; // country not in the list

      const province = this.fillProvinces(country);
      if (province?.dataset.default) province.value = province.dataset.default;
    });
  }

  // Rebuilds the state list of the country's own address block and returns that select.
  // The field always stays visible; countries without states leave it empty.
  fillProvinces(countrySelect) {
    const province = countrySelect.closest('[data-quote-address]')?.querySelector('[data-quote-province] select');
    if (!province) return null;

    let provinces = [];
    try {
      provinces = JSON.parse(countrySelect.selectedOptions[0]?.getAttribute('data-provinces') || '[]');
    } catch {
      provinces = [];
    }

    province.replaceChildren(...provinces.map(([value, name]) => new Option(name, value)));
    return province;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
